import React, { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, reauthenticateWithCredential, EmailAuthProvider, reauthenticateWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { db, functions } from '../firebase';
import { collection, addDoc, query, where, getDocs, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import MapPicker from '../components/MapPicker';

function ClientDashboard() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [checkingRole, setCheckingRole] = useState(true);
  const { useNavigate } = require('react-router-dom');
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ trade: '', parish: '', address: '', details: '' });
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const trades = [
    'Plumbing', 'Electrical', 'Landscaping', 'Painting', 'Carpentry', 'Roofing', 'Masonry', 'Other'
  ];
  const parishes = [
    'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary', 'St. Ann',
    'Trelawny', 'St. James', 'Hanover', 'Westmoreland', 'St. Elizabeth',
    'Manchester', 'Clarendon', 'St. Catherine'
  ];

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        // Check user role in Firestore by UID (avoid list queries)
          let userRole = null;
          try {
            const userRef = doc(db, 'users', u.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              userRole = userSnap.data().role;
            }
            setRole(userRole);
          } catch (err) {
            console.error('Error checking user role by UID:', err);
            setError(err && err.message ? err.message : 'Failed to check user role.');
            setCheckingRole(false);
            return;
          }
          setCheckingRole(false);
          if (userRole !== 'client') {
          navigate('/');
          return;
        }
        // Fetch this client's requests
        try {
          const q = query(collection(db, 'maintenanceRequests'), where('clientEmail', '==', u.email));
          const snap = await getDocs(q);
          setRequests(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
          console.error('Error fetching client requests:', err);
          setError(err && err.message ? err.message : 'Failed to fetch requests.');
        }
      } else {
        setCheckingRole(false);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await addDoc(collection(db, 'maintenanceRequests'), {
        ...form,
        clientEmail: user.email,
        submittedAt: serverTimestamp(),
        status: 'open',
        location,
      });
      setForm({ trade: '', parish: '', address: '', details: '' });
      setLocation(null);
      // Refresh requests
      try {
        const q = query(collection(db, 'maintenanceRequests'), where('clientEmail', '==', user.email));
        const snap = await getDocs(q);
        setRequests(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error('Error refreshing requests after submit:', err);
        setError(err && err.message ? err.message : 'Failed to refresh requests.');
      }
    } catch (err) {
      console.error('Error submitting request:', err);
      setError(err && err.message ? err.message : 'Failed to submit request.');
    }
    setLoading(false);
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    const confirmed = window.confirm('This will permanently delete your account and data. Continue?');
    if (!confirmed) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const authInstance = getAuth();
      const current = authInstance.currentUser;
      if (!current) throw new Error('No signed-in user.');
      const providerId = current.providerData?.[0]?.providerId || 'password';

      if (providerId === 'password') {
        const pwd = window.prompt('Confirm your password to delete your account:');
        if (!pwd) {
          setDeleting(false);
          return;
        }
        const credential = EmailAuthProvider.credential(current.email, pwd);
        await reauthenticateWithCredential(current, credential);
      } else if (providerId === 'google.com') {
        await reauthenticateWithPopup(current, new GoogleAuthProvider());
      }

      // Force fresh ID token and send it explicitly to the callable
      const token = await current.getIdToken(true);

      const fn = httpsCallable(functions, 'deleteAccount');
      await fn({ idToken: token });
      await signOut(authInstance);
      navigate('/');
    } catch (err) {
      console.error('Delete account failed', err);
      setDeleteError(err?.message || 'Failed to delete account.');
    }
    setDeleting(false);
  };

  const handleLogout = async () => {
    try {
      const authInstance = getAuth();
      await signOut(authInstance);
      navigate('/client-auth');
    } catch (err) {
      console.error('Logout failed', err);
    }
  };

  const handleAcceptQuote = async (requestId) => {
    setAcceptingId(requestId);
    setError('');
    try {
      const reqRef = doc(db, 'maintenanceRequests', requestId);
      await updateDoc(reqRef, {
        quoteAccepted: true,
        quoteAcceptedAt: serverTimestamp(),
      });
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, quoteAccepted: true, quoteAcceptedAt: new Date().toISOString() } : r));
    } catch (err) {
      console.error('Accept quote failed', err);
      setError(err?.message || 'Failed to accept quote.');
    }
    setAcceptingId(null);
  };

  if (checkingRole) return <p style={{textAlign:'center',marginTop:'3rem'}}>Checking access...</p>;
  if (!user) return <p style={{textAlign:'center',marginTop:'3rem'}}>Please log in as a client.</p>;
  if (role !== 'client') return null;

  return (
    <div style={{maxWidth:'600px',margin:'2rem auto',background:'#f3f8f6',borderRadius:'12px',boxShadow:'0 2px 8px rgba(0,0,0,0.07)',padding:'2rem'}}>
      <button className="mobile-dashboard-logout" onClick={handleLogout} aria-label="Logout">
        🚪 Logout
      </button>
      <h2 style={{color:'#007b5e',marginBottom:'1.5rem',textAlign:'center'}}>Client Dashboard</h2>
      <div style={{textAlign:'center',marginBottom:'1rem'}}>
        <button
          onClick={() => setShowForm(v => !v)}
          style={{
            background: showForm ? '#0a6fa9' : '#007b5e',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '0.8rem 1.4rem',
            fontWeight: 'bold',
            fontSize: '1rem',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
          }}
        >
          {showForm ? 'Hide form' : 'Submit New Job Request'}
        </button>
      </div>

      {showForm && (
        <>
          <h3 style={{color:'#007b5e',marginBottom:'1rem'}}>Submit a New Job Request</h3>
          <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',gap:'1rem',marginBottom:'2rem'}}>
            <select name="trade" value={form.trade} onChange={handleChange} required style={{padding:'0.7rem',borderRadius:'6px',border:'1px solid #ccc'}}>
              <option value="">Select Trade</option>
              {trades.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select name="parish" value={form.parish} onChange={handleChange} required style={{padding:'0.7rem',borderRadius:'6px',border:'1px solid #ccc'}}>
              <option value="">Select Parish</option>
              {parishes.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <input name="address" value={form.address} onChange={handleChange} placeholder="Address" required style={{padding:'0.7rem',borderRadius:'6px',border:'1px solid #ccc'}} />
            <textarea name="details" value={form.details} onChange={handleChange} placeholder="Details" required style={{padding:'0.7rem',borderRadius:'6px',border:'1px solid #ccc',minHeight:'80px'}} />
            <div style={{textAlign:'left'}}>
              <p style={{margin:'0 0 0.5rem 0', fontWeight:'600', color:'#007b5e'}}>Pin your location (optional)</p>
              <MapPicker apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} value={location} onChange={setLocation} />
            </div>
            {error && <div style={{color:'#d9534f',textAlign:'center'}}>{error}</div>}
            <button type="submit" disabled={loading} style={{background:'linear-gradient(90deg,#007b5e 60%,#00c6a7 100%)',color:'white',border:'none',borderRadius:'6px',padding:'0.8rem',fontWeight:'bold',fontSize:'1rem',cursor:'pointer'}}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        </>
      )}
      <h3 style={{color:'#007b5e',marginBottom:'1rem'}}>Your Job Requests</h3>
      {requests.length === 0 ? (
        <p>No requests yet.</p>
      ) : (
        <ul style={{listStyle:'none',padding:0}}>
          {requests.map(r => (
            <li key={r.id} style={{background:'#fff',borderRadius:'8px',boxShadow:'0 1px 4px rgba(0,0,0,0.04)',marginBottom:'1rem',padding:'1rem'}}>
              <strong>{r.trade}</strong> - {r.parish}<br />
              <span>{r.address}</span><br />
              <span>{r.details}</span><br />
              <span>Status: <b style={{color:'#007b5e'}}>{r.status}</b></span><br />
              {r.price && (
                <span>
                  Quote{r.priceType ? ` (${r.priceType})` : ''}: <b style={{color:'#007b5e'}}>{r.price}</b>
                </span>
              )}<br />
              {r.price && (
                <div style={{marginTop:'0.5rem'}}>
                  {r.quoteAccepted ? (
                    <span style={{color:'#007b5e',fontWeight:'bold'}}>
                      Quote accepted{r.quoteAcceptedAt && r.quoteAcceptedAt.seconds ? ` on ${new Date(r.quoteAcceptedAt.seconds * 1000).toLocaleString()}` : ''}
                    </span>
                  ) : (
                    <button
                      onClick={() => handleAcceptQuote(r.id)}
                      disabled={acceptingId === r.id}
                      style={{
                        background: '#007b5e',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.4rem 0.9rem',
                        fontWeight: 'bold',
                        cursor: acceptingId === r.id ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {acceptingId === r.id ? 'Accepting...' : 'Accept Quote'}
                    </button>
                  )}
                </div>
              )}
              <span>Submitted: {r.submittedAt && r.submittedAt.seconds ? new Date(r.submittedAt.seconds * 1000).toLocaleString() : ''}</span>
            </li>
          ))}
        </ul>
      )}
      <div style={{marginTop:'2rem',padding:'1rem',border:'1px solid #f5c6cb',borderRadius:'8px',background:'#fff5f5'}}>
        <h4 style={{color:'#c0392b',marginBottom:'0.5rem'}}>Delete account</h4>
        <p style={{marginBottom:'0.75rem'}}>This removes your account, requests, and stored data. This cannot be undone.</p>
        {deleteError && <div style={{color:'#c0392b',marginBottom:'0.5rem'}}>{deleteError}</div>}
        <button onClick={handleDeleteAccount} disabled={deleting} style={{background:'#c0392b',color:'#fff',border:'none',borderRadius:'6px',padding:'0.75rem 1rem',cursor:'pointer',fontWeight:'bold'}}>
          {deleting ? 'Deleting...' : 'Delete my account'}
        </button>
      </div>
    </div>
  );
}

export default ClientDashboard;
